import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import type { KitDto } from '../../shared/api';
import { get, post, readPhoto, refresh } from '../api';
import { Err, Spinner, Tag, Where, errMsg, hm, useApp, ymd } from '../components/ui';

type State = 'ok' | 'missing' | 'broken';
const ST: Record<State, [string, string]> = { ok: ['ok', 'In suitcase'], missing: ['bad', 'Missing'], broken: ['warn', 'Broken'] };

export function Kit() {
  const { d, toast } = useApp();
  const [sp, setSp] = useSearchParams();
  const kit = useQuery({ queryKey: ['kit'], queryFn: () => get<KitDto | null>('/api/kit') });
  const initial = sp.get('check');
  const [mode, setMode] = useState<string | null>(initial === 'before' ? 'Check before installation' : initial === 'return' ? 'Return check' : null);
  const [marks, setMarks] = useState<Record<number, State>>({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [nk, setNk] = useState({ name: '', qty: 1, specs: '', link: '' });
  const [nkFile, setNkFile] = useState<File | null>(null);
  const [kitNo, setKitNo] = useState('');

  const run = async (fn: () => Promise<{ text?: string } | unknown>, msg?: string) => {
    setErr('');
    setBusy(true);
    try {
      const r = (await fn()) as { text?: string } | null;
      await refresh();
      const m = r?.text ?? msg;
      if (m) toast(m);
      return true;
    } catch (e) {
      setErr(errMsg(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const photo = async (id: number, file?: File) => {
    if (!file) return;
    try {
      const p = await readPhoto(file);
      await run(() => post(`/api/kit/contents/${id}/photo`, { photo: p }), 'Photo saved');
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  if (kit.isLoading) return <Spinner />;
  const k = kit.data;
  if (!k)
    return (
      <>
        <h1>Installation suitcase</h1>
        <p className="sub">There is one installation suitcase. First register the suitcase itself as an item (storage type 1-2, home S3). Then the warehouse owner links it here.</p>
        {d.isOwner ? (
          <div className="panel row" style={{ alignItems: 'flex-end' }}>
            <label className="field" style={{ minWidth: 240 }}>
              Item number of the suitcase
              <input list="kitlist" placeholder="PH-26-07-…" value={kitNo} onChange={(e) => setKitNo(e.target.value)} />
              <datalist id="kitlist">
                {d.b.items
                  .filter((i) => d.artOf(i).type === '1-2')
                  .map((i) => (
                    <option key={i.no} value={i.no}>
                      {d.artOf(i).name}
                    </option>
                  ))}
              </datalist>
            </label>
            <button className="btn primary" disabled={busy} onClick={() => void run(() => post('/api/kit/item', { no: kitNo }), 'Suitcase linked')}>
              Link the suitcase
            </button>
          </div>
        ) : (
          <p className="meta">No suitcase yet.</p>
        )}
        <Err e={err} />
      </>
    );

  const sc = d.item(k.no);
  const todo = k.contents.filter((c) => c.state !== 'ok');
  const last = k.checks[0];
  const n = k.contents.length;
  const done = k.contents.filter((c) => marks[c.id]).length;
  const stopCheck = () => {
    setMode(null);
    setMarks({});
    if (sp.has('check')) setSp({});
  };

  return (
    <>
      <h1>Installation suitcase</h1>
      <p className="sub">
        The suitcase is its own project (code: {d.b.suitcaseProject || 'still to be added'}). Its tools have 07 tool numbers. Check it with these photos when you bring the suitcase back, and again before the next installation. The Checker on duty confirms each check.
      </p>
      <div className="panel row" style={{ alignItems: 'center', marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <b>{k.name}</b> <span className="code">{k.no}</span>
          <br />
          {sc && <Where i={sc} />}
          <br />
          <span className="meta">{last ? `Last check: ${last.type.toLowerCase()} by ${last.by} on ${ymd(last.at)}, ${hm(last.at)}` : 'Not checked yet'}</span>
        </div>
        {!mode && (
          <div className="row">
            <button className="btn primary" onClick={() => setMode('Return check')}>
              Check after return
            </button>
            <button className="btn" onClick={() => setMode('Check before installation')}>
              Check before installation
            </button>
          </div>
        )}
      </div>
      {mode && (
        <div className="checkbar">
          <b>{mode}</b>
          <span>
            Look at each photo and mark the item. {done} of {n} done.
          </span>
          <span style={{ marginLeft: 'auto' }} className="row">
            <button className="btn small" onClick={stopCheck}>
              Cancel
            </button>
            <button
              className="btn small"
              style={{ background: 'var(--bg)', color: 'var(--ink)' }}
              disabled={done < n || busy}
              onClick={async () => {
                if (await run(() => post('/api/kit/checks', { type: mode, marks: Object.fromEntries(Object.entries(marks).map(([id, v]) => [id, v])) }))) stopCheck();
              }}
            >
              Save check
            </button>
          </span>
        </div>
      )}
      <Err e={err} />
      <div className="kgrid">
        {k.contents.map((c) => {
          const m = marks[c.id];
          return (
            <div key={c.id} className={`kcard ${mode ? '' : c.state}`}>
              {c.photo ? (
                <img className="kpic" src={c.photo} alt={c.name} />
              ) : (
                <label className="nopic" style={{ cursor: 'pointer' }}>
                  <span>
                    <b style={{ color: 'var(--bad)' }}>Photo needed</b>
                    <br />
                    Tap to add a photo
                    <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => void photo(c.id, e.target.files?.[0])} />
                  </span>
                </label>
              )}
              <div>
                <b>{c.name}</b>
                <br />
                <span className="code">{c.no}</span>{' '}
                <span className="meta">
                  {c.qty} pc{c.qty > 1 ? 's' : ''}
                </span>
              </div>
              {mode ? (
                <div className="marks">
                  <button className={`btn ok ${m === 'ok' ? 'on' : ''}`} onClick={() => setMarks({ ...marks, [c.id]: 'ok' })}>
                    ✓ There
                  </button>
                  <button className={`btn bad ${m === 'missing' ? 'on' : ''}`} onClick={() => setMarks({ ...marks, [c.id]: 'missing' })}>
                    Missing
                  </button>
                  <button className={`btn warn ${m === 'broken' ? 'on' : ''}`} onClick={() => setMarks({ ...marks, [c.id]: 'broken' })}>
                    Broken
                  </button>
                </div>
              ) : (
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <Tag cls={ST[c.state][0]} dot>
                    {ST[c.state][1]}
                  </Tag>
                  {c.photo && (
                    <label className="btn small">
                      Change photo
                      <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => void photo(c.id, e.target.files?.[0])} />
                    </label>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <h2>To buy or replace ({todo.length})</h2>
      <p className="meta" style={{ margin: '-4px 0 10px' }}>
        The project manager buys these items and marks them as replaced. A new tool gets a new 07 number; the old number stays with the broken or lost tool.
      </p>
      {todo.length ? (
        <div className="panel scroll" style={{ padding: '4px 8px' }}>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Problem</th>
                <th>Qty</th>
                <th>Specs</th>
                <th>Where to buy</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {todo.map((c) => (
                <tr key={c.id}>
                  <td>
                    <b>{c.name}</b>
                    <br />
                    <span className="code">{c.no}</span>
                  </td>
                  <td>
                    <Tag cls={ST[c.state][0]} dot>
                      {ST[c.state][1]}
                    </Tag>
                  </td>
                  <td>{c.qty}</td>
                  <td>{c.specs}</td>
                  <td>
                    {c.link && (
                      <>
                        <a href={c.link} target="_blank" rel="noopener noreferrer">
                          Supplier
                        </a>
                        ,{' '}
                      </>
                    )}
                    <a href={`https://www.google.com/search?q=${encodeURIComponent(c.name + ' ' + c.specs)}`} target="_blank" rel="noopener noreferrer">
                      find similar
                    </a>
                  </td>
                  <td>
                    <button className="btn small" disabled={busy} onClick={() => void run(() => post(`/api/kit/contents/${c.id}/replaced`))}>
                      Replaced, back in suitcase
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="meta">Nothing to buy. The suitcase is complete.</p>
      )}
      <h2>Check history</h2>
      <div className="panel">
        {k.checks.length ? (
          k.checks.map((c) => (
            <div key={c.id} className="note">
              <span className="meta">
                {ymd(c.at)}, {hm(c.at)} · {c.by} · {c.type}
              </span>
              {c.desc}
              <br />
              {c.confirmedBy ? (
                <Tag cls="ok">Confirmed by {c.confirmedBy}</Tag>
              ) : (
                <>
                  <Tag cls="warn">Waiting for the Checker</Tag>{' '}
                  {d.me.isChecker && (
                    <button className="btn small" disabled={busy} onClick={() => void run(() => post(`/api/kit/checks/${c.id}/confirm`), 'Check confirmed')}>
                      Confirm as Checker
                    </button>
                  )}
                </>
              )}
            </div>
          ))
        ) : (
          <span className="meta">No checks yet.</span>
        )}
      </div>
      {d.isOwner && (
        <>
          <h2>Add an item to the suitcase</h2>
          <div className="panel row" style={{ alignItems: 'flex-end' }}>
            <label className="field" style={{ flex: 2, minWidth: 200 }}>
              Name
              <input placeholder="e.g. Crimping tool" value={nk.name} onChange={(e) => setNk({ ...nk, name: e.target.value })} />
            </label>
            <label className="field" style={{ width: 80 }}>
              Qty
              <input type="number" min={1} value={nk.qty} onChange={(e) => setNk({ ...nk, qty: Math.max(1, parseInt(e.target.value, 10) || 1) })} />
            </label>
            <label className="field" style={{ flex: 2, minWidth: 180 }}>
              Specs
              <input placeholder="Size, model" value={nk.specs} onChange={(e) => setNk({ ...nk, specs: e.target.value })} />
            </label>
            <label className="field" style={{ flex: 2, minWidth: 180 }}>
              Purchase link
              <input placeholder="https://" value={nk.link} onChange={(e) => setNk({ ...nk, link: e.target.value })} />
            </label>
            <label className="field">
              Photo (required)
              <input type="file" accept="image/*" capture="environment" onChange={(e) => setNkFile(e.target.files?.[0] ?? null)} />
            </label>
            <button
              className="btn primary"
              disabled={busy}
              onClick={async () => {
                if (!nk.name.trim()) return setErr('Add a name');
                if (!nkFile) return setErr('Add a photo of the item');
                try {
                  const p = await readPhoto(nkFile);
                  if (await run(() => post('/api/kit/contents', { ...nk, photo: p }))) {
                    setNk({ name: '', qty: 1, specs: '', link: '' });
                    setNkFile(null);
                  }
                } catch (e) {
                  setErr(errMsg(e));
                }
              }}
            >
              Add to suitcase
            </button>
          </div>
          <p className="meta">The app gives the next free 07 number.</p>
        </>
      )}
    </>
  );
}
